import axios from "axios";
import CryptoJS from 'crypto-js';
import { getToken, getTokenPayload, logout } from "./session";

const ENCRYPTION_KEY = CryptoJS.enc.Utf8.parse("12345678901234567890123456789012");
const ENCRYPTION_IV = CryptoJS.enc.Utf8.parse("1234567890123456");
// Empty base = same-origin requests, served through the Vite dev proxy
// (see vite.config.js). Production builds set VITE_API_URL to the real API.
// const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:9265";
// const API_BASE = import.meta.env.VITE_API_URL || "https://meanstack.smartdatainc.com:9363";
const API_BASE = import.meta.env.VITE_API_URL || "https://api.onepassblueprint.com";

// Attach a valid (non-expired) token to every request. getToken() purges an
// expired token, so a stale JWT is never sent.
axios.interceptors.request.use((config) => {
    const token = getToken();
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// If the server rejects us (expired/invalid JWT), tear the session down and
// bounce to login so the UI never lingers in a fake logged-in state.
axios.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            logout();
        }
        return Promise.reject(error);
    }
);

// Shared guard for the fetch()-based streaming calls, which bypass the axios
// interceptors above.
const throwIfUnauthorized = (response) => {
    if (response.status === 401) {
        logout();
        throw new Error("Session expired. Please sign in again.");
    }
};
export const extractText = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    const response = await axios.post(`${API_BASE}/api/v1/validations/extract-text`, formData, {
        headers: {
            "Content-Type": "multipart/form-data"
        }
    });
    return response.data;
};

/**
 * Auth APIs
 */
// OTP mail goes out over SMTP, which normally takes ~2s. Give up after a
// minute so the form never sits on "Sending OTP..." forever.
const OTP_REQUEST_TIMEOUT_MS = 60_000;

export const sendOtp = async (email) => {
    const response = await axios.post(`${API_BASE}/api/v1/auth/send-otp`, {
        email
    }, { timeout: OTP_REQUEST_TIMEOUT_MS });
    return response.data;
};

export const sendForgotPasswordOtp = async (email) => {
    const response = await axios.post(`${API_BASE}/api/v1/auth/forgot-password/send-otp`, {
        email
    }, { timeout: OTP_REQUEST_TIMEOUT_MS });
    return response.data;
};

/** Message for a failed OTP request: the server's reason when it gave one,
 *  otherwise a plain "could not reach the server". */
export const otpErrorMessage = (err) =>
    err?.response?.data?.detail ||
    (err?.response ? 'Failed to send OTP' : 'Could not reach the server. Please check the connection and try again.');

export const resetPassword = async (email, otp, new_password) => {
    const response = await axios.post(`${API_BASE}/api/v1/auth/forgot-password/reset`, {
        email,
        otp,
        new_password
    });
    return response.data;
};

export const registerUser = async (name, email, phone, password, otp) => {
    const response = await axios.post(`${API_BASE}/api/v1/auth/register`, {
        name,
        email,
        phone_number: phone,
        password,
        otp
    });
    return response.data;
};

export const loginUser = async (email, password) => {
    const payload = JSON.stringify({ email, password });
    const encrypted = CryptoJS.AES.encrypt(payload, ENCRYPTION_KEY, {
        iv: ENCRYPTION_IV,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7
    });

    const response = await axios.post(`${API_BASE}/api/v1/auth/login`, {
        encrypted_data: encrypted.toString()
    });

    const encryptedResponse = response.data.encrypted_data;
    const decryptedBytes = CryptoJS.AES.decrypt(encryptedResponse, ENCRYPTION_KEY, {
        iv: ENCRYPTION_IV,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7
    });

    const decryptedStr = decryptedBytes.toString(CryptoJS.enc.Utf8);
    return JSON.parse(decryptedStr);
};

export const getMe = async () => {
    const response = await axios.get(`${API_BASE}/api/v1/auth/me`);
    return response.data;
};

/**
 * Payment APIs
 */
export const createCheckoutSession = async (userEmail, productType, mapId = null) => {
    const response = await axios.post(`${API_BASE}/api/v1/payments/create-checkout-session`, {
        user_email: userEmail,
        product_type: productType,
        map_id: mapId
    });
    return response.data;
};

export const getPaymentHistory = async () => {
    const response = await axios.get(`${API_BASE}/api/v1/payments/history`);
    return response.data;
};

export const verifyCheckoutSession = async (sessionId) => {
    const response = await axios.post(`${API_BASE}/api/v1/payments/verify-session`, { session_id: sessionId });
    return response.data;
};

/**
 * Fetch Architectural Rules from the backend.
 */
export const getArchitecturalRules = async () => {
    const response = await axios.get(`${API_BASE}/api/v1/rules/architectural`);
    return response.data;
};

/**
 * Fetch Deterministic Rules from the backend.
 */
export const getDeterministicRules = async () => {
    const response = await axios.get(`${API_BASE}/api/v1/rules/deterministic`);
    return response.data;
};

/**
 * Validate Intake Request (Meaning Validator)
 */
export const validateIntake = async (rawRequest, normalizedRequest = null, sessionId = null) => {
    const payload = { raw_request: rawRequest };
    if (normalizedRequest !== null) {
        payload.normalized_request = normalizedRequest;
    }
    if (sessionId !== null) {
        payload.session_id = sessionId;
    }
    const response = await axios.post(`${API_BASE}/api/v1/validations/validate-intake`, payload, {
        headers: { "Content-Type": "application/json" }
    });
    return response.data;
};

/**
 * Analyze user intent: extract intent, pattern, behavior, entities, variables.
 */
export const analyzeIntent = async (rawRequest, sessionId = null) => {
    let userName = "Guest";
    const payload = getTokenPayload();
    if (payload?.name) userName = payload.name;

    const response = await axios.post(`${API_BASE}/api/v1/generation/analyze-intent`, {
        raw_request: rawRequest,
        session_id: sessionId,
        user_name: userName
    }, { headers: { "Content-Type": "application/json" } });
    return response.data;
};

/**
 * Generate Map JSON from Intake Intent + optional intent_summary
 */
export const generateMapFromIntent = async (normalizedRequest, intentSummary = null) => {
    const response = await axios.post(`${API_BASE}/api/v1/generation/generate-map`, {
        normalized_request: normalizedRequest,
        intent_summary: intentSummary
    }, { headers: { "Content-Type": "application/json" } });
    return response.data;
};

/**
 * Stream Map JSON from Intake Intent + optional intent_summary
 */
export const generateMapStream = async (normalizedRequest, intentSummary = null) => {
    const token = getToken();
    const headers = { "Content-Type": "application/json" };
    if (token) headers["Authorization"] = `Bearer ${token}`;

    const response = await fetch(`${API_BASE}/api/v1/generation/generate-map-stream`, {
        method: "POST",
        headers,
        body: JSON.stringify({
            normalized_request: normalizedRequest,
            intent_summary: intentSummary
        }),
    });

    throwIfUnauthorized(response);
    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.body;
};

/**
 * Generate Micro Intelligence Map
 */
export const generateMicroIntelligenceMap = async (documentSignals, tenantId = "tenant-1234") => {
    const response = await axios.post(`${API_BASE}/api/v1/intelligence/map/create`, {
        document_signals: documentSignals,
        tenant_id: tenantId
    }, { headers: { "Content-Type": "application/json" } });
    return response.data;
};

/**
 * Update Session with Micro Intelligence Map Result
 */
export const updateSessionMicroMap = async (sessionId, microMap) => {
    const response = await axios.post(`${API_BASE}/api/v1/generation/update-micro-map`, {
        session_id: sessionId,
        micro_map: microMap
    }, { headers: { "Content-Type": "application/json" } });
    return response.data;
};

/**
 * HCL-Lite View
 */
export const getHCLView = async (sessionId, roleName, tenantId = "default-tenant") => {
    const response = await axios.post(`${API_BASE}/api/v1/hcl/view`, {
        session_id: sessionId,
        role_name: roleName,
        tenant_id: tenantId
    }, { headers: { "Content-Type": "application/json" } });
    return response.data;
};

export const askHCLQuestion = async (sessionId, roleName, question, mode = "understanding", tenantId = "default-tenant") => {
    const response = await axios.post(`${API_BASE}/api/v1/hcl/ask`, {
        session_id: sessionId,
        role_name: roleName,
        tenant_id: tenantId,
        question,
        mode
    }, { headers: { "Content-Type": "application/json" } });
    return response.data;
};

/**
 * Developer Intelligence Dashboard
 */
export const getDeveloperDashboardSummary = async (sessionId, tenantId = "default-tenant") => {
    const response = await axios.post(`${API_BASE}/api/v1/developer-dashboard/summary`, {
        session_id: sessionId,
        tenant_id: tenantId
    }, { headers: { "Content-Type": "application/json" } });
    return response.data;
};

export const explainRequirement = async (sessionId, requirementId, tenantId = "default-tenant") => {
    const response = await axios.post(`${API_BASE}/api/v1/developer-dashboard/explain`, {
        session_id: sessionId,
        requirement_id: requirementId,
        tenant_id: tenantId
    }, { headers: { "Content-Type": "application/json" } });
    return response.data;
};

/**
 * Validate a canonical map JSON (ALL rules).
 */
export const validateMap = async (mapJson) => {
    const response = await axios.post(
        `${API_BASE}/api/v1/validations/validate-map`,
        mapJson,
        {
            headers: { "Content-Type": "application/json" },
            params: { use_local_fallback: false }
        }
    );
    return response.data;
};

/**
 * Validate a canonical map using ONLY critical AR + blocker DET rules.
 * Ignores warnings and minor errors for fast, actionable feedback.
 */
export const validateMapStrict = async (mapJson) => {
    const response = await axios.post(
        `${API_BASE}/api/v1/validations/validate-map-strict`,
        mapJson,
        { headers: { "Content-Type": "application/json" } }
    );
    return response.data;
};

/**
 * Stream code generation from Claude.
 * Returns a ReadableStream.
 */
export const streamCode = async (mapJson) => {
    const token = getToken();
    const headers = { "Content-Type": "application/json" };
    if (token) headers["Authorization"] = `Bearer ${token}`;

    const response = await fetch(`${API_BASE}/api/v1/generation/claude/stream`, {
        method: "POST",
        headers,
        body: JSON.stringify({ map_json: mapJson }),
    });

    throwIfUnauthorized(response);
    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.body;
};

/**
 * Stream code generation directly from Intent Summary (Shortcut Flow).
 */
export const streamCodeFromIntent = async (intentSummary) => {
    const token = getToken();
    const headers = { "Content-Type": "application/json" };
    if (token) headers["Authorization"] = `Bearer ${token}`;

    const response = await fetch(`${API_BASE}/api/v1/generation/claude/from-intent/stream`, {
        method: "POST",
        headers,
        body: JSON.stringify({ intent_summary: intentSummary }),
    });

    throwIfUnauthorized(response);
    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.body;
};

/**
 * Export blueprint directly to VS Code or Cursor.
 */
export const exportToVSCode = async (mapText, mapId = null, editor = "code") => {
    const response = await axios.post(`${API_BASE}/api/v1/generation/export-vscode`, {
        map_text: mapText,
        map_id: mapId,
        editor: editor
    }, { headers: { "Content-Type": "application/json" } });
    return response.data;
};

/**
 * Diagnose runtime error.
 */
export const diagnoseError = async (runError, generatedCode) => {
    const response = await axios.post(`${API_BASE}/api/v1/generation/diagnose-error`, {
        run_error: runError,
        generated_code: generatedCode
    }, { headers: { "Content-Type": "application/json" } });
    return response.data;
};

/**
 * Submit a custom quote request. The backend emails the details (plus the
 * attached query/blueprint) to the quote recipient, with the user's email set
 * as Reply-To so replies go straight back to them.
 */
export const submitQuoteRequest = async ({ name, email, phone, query, blueprint }) => {
    const response = await axios.post(`${API_BASE}/api/v1/quote`, {
        name,
        email,
        phone,
        query,
        blueprint
    }, { headers: { "Content-Type": "application/json" } });
    return response.data;
};

/**
 * Get all sessions for the dashboard.
 */
export const getSessions = async () => {
    const response = await axios.get(`${API_BASE}/api/v1/dashboard/sessions`);
    return response.data;
};

/**
 * Get all users with their payment history for the admin panel.
 */
export const getAdminUsers = async () => {
    const response = await axios.get(`${API_BASE}/api/v1/dashboard/admin/users`);
    return response.data;
};

/**
 * Get all expert support quotes for the admin panel.
 */
export const getAdminQuotes = async () => {
    const response = await axios.get(`${API_BASE}/api/v1/dashboard/admin/quotes`);
    return response.data;
};

/**
 * Final Blueprint Outputs APIs
 *
 * Every output (developer JSON, human blueprint, executive summary, diagrams)
 * is generated server-side from ONE final approved blueprint. Two modes:
 *  - persisted: approve-map stored the blueprint and returned a blueprint_id
 *  - preview:   the locked blueprint JSON travels in the request body
 *    (guest sessions, where approval is client-side only)
 */

/**
 * Persist the approved blueprint server-side (requires login + a valid
 * validation token). Returns { status, message, blueprint_id, map_id, version }.
 */
export const approveMapOnServer = async (mapJson, validationToken, sessionId = null) => {
    const response = await axios.post(`${API_BASE}/api/v1/approvals/approve-map`, {
        validation_token: validationToken,
        map_json: mapJson,
        session_id: sessionId
    }, { headers: { "Content-Type": "application/json" } });
    return response.data;
};

/**
 * Generate all four coordinated outputs of the final approved blueprint.
 * Uses the persisted blueprint when blueprintId is provided, otherwise the
 * stateless preview endpoint with the locked blueprint JSON.
 */
export const getBlueprintOutputs = async ({ blueprintId = null, mapJson = null, sessionId = null }) => {
    const response = blueprintId
        ? await axios.get(`${API_BASE}/api/v1/blueprints/${blueprintId}/outputs`)
        : await axios.post(`${API_BASE}/api/v1/blueprints/preview/outputs`,
            { map_json: mapJson, session_id: sessionId },
            { headers: { "Content-Type": "application/json" } });
    return response.data;
};

/**
 * Download one of the blueprint PDFs ("human-blueprint" | "executive-summary")
 * as a Blob.
 */
export const downloadBlueprintPdf = async ({ kind, blueprintId = null, mapJson = null }) => {
    const response = blueprintId
        ? await axios.get(`${API_BASE}/api/v1/blueprints/${blueprintId}/${kind}/pdf`,
            { responseType: "blob" })
        : await axios.post(`${API_BASE}/api/v1/blueprints/preview/${kind}/pdf`,
            { map_json: mapJson },
            { headers: { "Content-Type": "application/json" }, responseType: "blob" });
    return response.data;
};

/**
 * Second Pair of Eyes — Build Snapshot.
 * Validate a Snapshot file (parsed JSON) against the approved blueprint it
 * references. Returns { status, issues, summary, blueprint }.
 */
export const validateBuildSnapshot = async (snapshot) => {
    const response = await axios.post(`${API_BASE}/api/v1/build-snapshots/validate`,
        { snapshot },
        { headers: { "Content-Type": "application/json" } });
    return response.data;
};

/**
 * Snapshot instructions plus a template pre-filled with the blueprint's
 * Requirement IDs. Returns { blueprint, instructions }.
 */
export const getBuildSnapshotTemplate = async (blueprintId) => {
    const response = await axios.get(`${API_BASE}/api/v1/build-snapshots/template/${blueprintId}`);
    return response.data;
};

/**
 * Second Pair of Eyes — Check My Build.
 * Validates the Snapshot, compares it to the exact blueprint version it names
 * and stores the run. Rejected Snapshots come back as 404/422 with
 * `detail.message` and `detail.issues`.
 */
export const runBuildCheck = async (snapshot) => {
    const response = await axios.post(`${API_BASE}/api/v1/build-checks`,
        { snapshot },
        { headers: { "Content-Type": "application/json" } });
    return response.data;
};

/** Build Check history (newest first), optionally for one blueprint or project. */
export const listBuildChecks = async ({ blueprintId = null, projectId = null } = {}) => {
    const params = {};
    if (blueprintId) params.blueprint_id = blueprintId;
    if (projectId) params.project_id = projectId;
    const response = await axios.get(`${API_BASE}/api/v1/build-checks`, { params });
    return response.data;
};

/** One stored Build Check in full. */
export const getBuildCheck = async (checkId) => {
    const response = await axios.get(`${API_BASE}/api/v1/build-checks/${checkId}`);
    return response.data;
};

/**
 * Recheck Build: an updated Snapshot checked against the same project,
 * blueprint version and findings as an earlier check. Returns the new check
 * with a `recheck` block describing how each earlier finding stands.
 */
export const recheckBuild = async (checkId, snapshot) => {
    const response = await axios.post(`${API_BASE}/api/v1/build-checks/${checkId}/recheck`,
        { snapshot },
        { headers: { "Content-Type": "application/json" } });
    return response.data;
};

/** One Correction Blueprint (generated with its check) with its version lineage. */
export const getCorrectionBlueprint = async (correctionId) => {
    const response = await axios.get(`${API_BASE}/api/v1/build-checks/corrections/${correctionId}`);
    return response.data;
};

/** A Correction Blueprint as a PDF Blob. */
export const downloadCorrectionBlueprintPdf = async (correctionId) => {
    const response = await axios.get(`${API_BASE}/api/v1/build-checks/corrections/${correctionId}/pdf`,
        { responseType: "blob" });
    return response.data;
};

/** The approved blueprint versions of one of the user's projects, newest first. */
export const getProjectBlueprints = async (projectId) => {
    const response = await axios.get(`${API_BASE}/api/v1/build-checks/blueprints`, {
        params: { project_id: projectId },
    });
    return response.data;
};

/**
 * Update the status of an expert support quote.
 */
export const updateQuoteStatus = async (quoteId, status) => {
    const token = getToken();
    const headers = { "Content-Type": "application/json" };
    if (token) headers["Authorization"] = `Bearer ${token}`;

    const response = await axios.put(`${API_BASE}/api/v1/dashboard/admin/quotes/${quoteId}/status`,
        { status },
        { headers }
    );
    return response.data;
};

/**
 * User Dashboard APIs.
 *
 * All of these are scoped server-side to the signed-in user via the JWT —
 * there is no user id in the URL, so one account can never request another
 * account's projects.
 */

/** The signed-in user's own saved projects (metadata only). */
export const getMyProjects = async () => {
    const response = await axios.get(`${API_BASE}/api/v1/me/projects`);
    return response.data;
};

/** One of the user's own projects, including its blueprint content. */
export const getMyProject = async (sessionId) => {
    const response = await axios.get(`${API_BASE}/api/v1/me/projects/${sessionId}`);
    return response.data;
};

/** Delete one of the user's own projects, and its revision history. */
export const deleteMyProject = async (sessionId) => {
    const response = await axios.delete(`${API_BASE}/api/v1/me/projects/${sessionId}`);
    return response.data;
};

/** Rename a project. */
export const renameMyProject = async (sessionId, title) => {
    const response = await axios.patch(`${API_BASE}/api/v1/me/projects/${sessionId}`, { title });
    return response.data;
};

/** Copy a project into a new one, with its own retention clock and history. */
export const duplicateMyProject = async (sessionId, title = null) => {
    const response = await axios.post(
        `${API_BASE}/api/v1/me/projects/${sessionId}/duplicate`,
        title ? { title } : {},
    );
    return response.data;
};

/**
 * Save everything needed to reopen this project later. The previous state is
 * kept as a revision, so the user can go back to it.
 */
export const saveMyProjectState = async (sessionId, workflowState, { settings = null, label = null, createRevision = true } = {}) => {
    const response = await axios.put(`${API_BASE}/api/v1/me/projects/${sessionId}/state`, {
        workflow_state: workflowState,
        settings,
        label,
        create_revision: createRevision,
    });
    return response.data;
};

/** A project's revision history, newest first. */
export const getMyProjectRevisions = async (sessionId) => {
    const response = await axios.get(`${API_BASE}/api/v1/me/projects/${sessionId}/revisions`);
    return response.data;
};

/** Roll a project back to an earlier revision. */
export const restoreMyProjectRevision = async (sessionId, revisionNumber) => {
    const response = await axios.post(
        `${API_BASE}/api/v1/me/projects/${sessionId}/revisions/${revisionNumber}/restore`,
    );
    return response.data;
};

/**
 * Download a project as a JSON file.
 *
 * Fetched through axios rather than a plain link so the request carries the
 * auth header; the blob is then handed to the browser as a download.
 */
export const exportMyProject = async (sessionId, filenameHint = "project") => {
    const response = await axios.get(`${API_BASE}/api/v1/me/projects/${sessionId}/export`, {
        responseType: "blob",
    });

    const disposition = response.headers?.["content-disposition"] || "";
    const match = disposition.match(/filename="?([^"]+)"?/);
    const filename = match ? match[1] : `${filenameHint}.json`;

    const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/json" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    return filename;
};

/** Headline counts and credits for the user dashboard. */
export const getMySummary = async () => {
    const response = await axios.get(`${API_BASE}/api/v1/me/summary`);
    return response.data;
};

/**
 * Billing APIs.
 *
 * Payment methods, invoices and cancellation happen in Stripe's hosted
 * portal — openBillingPortal returns the one-time link into it. Plan changes
 * go through our API so the agreed policy is enforced: upgrades apply
 * immediately with proration, downgrades at the next billing cycle.
 */

/** The subscription plans this environment can sell. */
export const getBillingPlans = async () => {
    const response = await axios.get(`${API_BASE}/api/v1/billing/plans`);
    return response.data;
};

/** The signed-in user's current subscription, or null. */
export const getMySubscription = async () => {
    const response = await axios.get(`${API_BASE}/api/v1/billing/subscription`);
    return response.data;
};

/**
 * A one-time link into Stripe's hosted billing portal.
 * `flow` can be "cancel" or "payment_method" to open straight onto that screen.
 */
export const openBillingPortal = async (flow = null, returnPath = "/build/billing") => {
    const response = await axios.post(`${API_BASE}/api/v1/billing/portal`, {
        flow,
        return_path: returnPath,
    });
    return response.data.url;
};

/** Start a first subscription through Stripe Checkout. */
export const startSubscription = async (planKey) => {
    const response = await axios.post(`${API_BASE}/api/v1/billing/checkout`, { plan_key: planKey });
    return response.data.url;
};

/** Upgrade (immediate, prorated) or downgrade (next billing cycle). */
export const changeSubscriptionPlan = async (planKey) => {
    const response = await axios.post(`${API_BASE}/api/v1/billing/change-plan`, { plan_key: planKey });
    return response.data;
};

/** Cancel at the end of the paid period. Access continues until then. */
export const cancelSubscription = async () => {
    const response = await axios.post(`${API_BASE}/api/v1/billing/cancel`);
    return response.data;
};

/** Undo a pending cancellation. */
export const resumeSubscription = async () => {
    const response = await axios.post(`${API_BASE}/api/v1/billing/resume`);
    return response.data;
};

/** Billing history, with links to view or download each invoice. */
export const getMyInvoices = async () => {
    const response = await axios.get(`${API_BASE}/api/v1/billing/invoices`);
    return response.data;
};

/**
 * Account APIs.
 *
 * Closing an account is a confirmed, reversible request: renewals stop and a
 * grace period starts, during which the user can reopen it.
 */

/** Everything the Account page shows, in one call. */
export const getMyAccount = async () => {
    const response = await axios.get(`${API_BASE}/api/v1/account`);
    return response.data;
};

/** Request account closure. `confirmation` must be the word CLOSE. */
export const closeMyAccount = async (confirmation, reason = null) => {
    const response = await axios.post(`${API_BASE}/api/v1/account/close`, { confirmation, reason });
    return response.data;
};

/** Change of mind during the grace period. */
export const reopenMyAccount = async () => {
    const response = await axios.post(`${API_BASE}/api/v1/account/reopen`);
    return response.data;
};
